// Inventory Screen – React + Tailwind (mobile‑first)
// ➡️ NOW tracks **Purchase Date** for every batch in addition to Qty, Price, Expiry.
// This date is critical for P&L period filters and FIFO/average‑cost calculations.

import React, { useEffect, useState } from "react";
import { inventoryApi } from "../api";

export default function Inventory({ user, subStatus, onSubscribe }) {
  /* ---------------------------------------------------------------------- */
  /*  STATE                                                                */
  /* ---------------------------------------------------------------------- */
  const [inventory, setInventory] = useState([]);

  const [selectedItem, setSelectedItem] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isAdding, setIsAdding] = useState(false);

  const defaultToday = new Date().toISOString().split("T")[0];
  const [newItem, setNewItem] = useState({
    name: "",
    quantity: "",
    price: "",
    purchaseDate: defaultToday,
    expiry: "",
  });
  const [newBatch, setNewBatch] = useState({
    quantity: "",
    price: "",
    purchaseDate: defaultToday,
    expiry: "",
  });

  useEffect(() => {
    (async () => {
      try {
        const items = await inventoryApi.listItems();
        const mapped = items.map((i) => ({
          id: i.itemId,
          name: i.name,
          quantity: i.totalQuantity || 0,
          expiryBatches: [],
        }));
        setInventory(mapped);
      } catch (e) {
        console.error("Failed to load inventory", e);
      }
    })();
  }, []);

  const recomputeQuantity = (batches) =>
    batches.reduce((sum, b) => sum + (parseFloat(b.quantity) || 0), 0);

  const handleAddItem = async () => {
    if (!newItem.name || !newItem.quantity || !newItem.price) return;
    try {
      const { item, batch } = await inventoryApi.createItem({
        name: newItem.name,
        initialBatch: {
          quantity: parseFloat(newItem.quantity),
          price: parseFloat(newItem.price),
          purchaseDate: newItem.purchaseDate,
          expiry: newItem.expiry || null,
        },
      });
      const itemToAdd = {
        id: item.itemId,
        name: item.name,
        quantity: item.totalQuantity,
        expiryBatches: [
          {
            id: batch.batchId,
            quantity: batch.quantity,
            price: batch.price,
            purchaseDate: newItem.purchaseDate,
            expiry: newItem.expiry || null,
          },
        ],
      };
      setInventory([...inventory, itemToAdd]);
      setNewItem({ name: "", quantity: "", price: "", purchaseDate: defaultToday, expiry: "" });
      setIsAdding(false);
    } catch (e) {
      console.error("Failed to create item", e);
    }
  };

  const handleAddBatch = async () => {
    if (!newBatch.quantity || !newBatch.price || !selectedItem) return;
    try {
      const { itemId } = { itemId: selectedItem.id };
      await inventoryApi.addBatch(itemId, {
        quantity: parseFloat(newBatch.quantity),
        price: parseFloat(newBatch.price),
        purchaseDate: newBatch.purchaseDate,
        expiry: newBatch.expiry || null,
      });
      // Refresh detail
      const detail = await inventoryApi.getItem(selectedItem.id);
      const mappedBatches = (detail.batches || []).map((b) => ({
        id: b.batchId,
        quantity: b.quantity,
        price: b.price,
        purchaseDate: (b.purchaseDate && b.purchaseDate._seconds)
          ? new Date(b.purchaseDate._seconds * 1000).toISOString().split("T")[0]
          : newBatch.purchaseDate,
        expiry: b.expiry && b.expiry._seconds
          ? new Date(b.expiry._seconds * 1000).toISOString().split("T")[0]
          : "",
      }));
      const updatedQuantity = detail.item?.totalQuantity || recomputeQuantity(mappedBatches);
      setSelectedItem({ ...selectedItem, expiryBatches: mappedBatches, quantity: updatedQuantity });
      setNewBatch({ quantity: "", price: "", purchaseDate: defaultToday, expiry: "" });
    } catch (e) {
      console.error("Failed to add batch", e);
    }
  };

  const handleDeleteBatch = async (idx) => {
    // Soft delete via active=false requires batchId; here we only have index.
    // Fetch latest detail, mark target batch inactive, then refresh.
    try {
      const detail = await inventoryApi.getItem(selectedItem.id);
      const target = detail.batches[idx];
      if (!target) return;
      await inventoryApi.updateBatch(selectedItem.id, target.batchId, { active: false });
      const refreshed = await inventoryApi.getItem(selectedItem.id);
      const mappedBatches = (refreshed.batches || []).map((b) => ({
        id: b.batchId,
        quantity: b.quantity,
        price: b.price,
        purchaseDate: b.purchaseDate?._seconds
          ? new Date(b.purchaseDate._seconds * 1000).toISOString().split("T")[0]
          : defaultToday,
        expiry: b.expiry?._seconds
          ? new Date(b.expiry._seconds * 1000).toISOString().split("T")[0]
          : "",
      }));
      const updatedQuantity = refreshed.item?.totalQuantity || recomputeQuantity(mappedBatches);
      setSelectedItem({ ...selectedItem, expiryBatches: mappedBatches, quantity: updatedQuantity });
    } catch (e) {
      console.error("Failed to delete batch", e);
    }
  };

  const openEdit = async (item) => {
    try {
      const detail = await inventoryApi.getItem(item.id || item.itemId);
      const mappedBatches = (detail.batches || []).map((b) => ({
        id: b.batchId,
        quantity: b.quantity,
        price: b.price,
        purchaseDate: b.purchaseDate?._seconds
          ? new Date(b.purchaseDate._seconds * 1000).toISOString().split("T")[0]
          : defaultToday,
        expiry: b.expiry?._seconds
          ? new Date(b.expiry._seconds * 1000).toISOString().split("T")[0]
          : "",
      }));
      setSelectedItem({ id: detail.item.itemId, name: detail.item.name, quantity: detail.item.totalQuantity, expiryBatches: mappedBatches });
      setIsEditing(true);
    } catch (e) {
      console.error("Failed to load item detail", e);
    }
  };

  const handleSaveEdit = async () => {
    // For now, allow rename only (quantity recalculated server-side from batches)
    try {
      await inventoryApi.updateItem(selectedItem.id, { name: selectedItem.name });
      setInventory((prev) => prev.map((it) => (it.id === selectedItem.id ? { ...it, name: selectedItem.name, quantity: selectedItem.quantity } : it)));
      setIsEditing(false);
      setSelectedItem(null);
    } catch (e) {
      console.error("Failed to save item", e);
    }
  };

  if (isEditing && selectedItem) {
    return (
      <div className="min-h-screen bg-white text-gray-900 p-4 font-sans">
        <h1 className="text-lg font-bold mb-3">Edit – {selectedItem.name}</h1>

        <div className="mb-4 border p-3 rounded-lg bg-green-50">
          <p className="text-sm font-semibold mb-1">Add Purchase Batch</p>
          <div className="mt-2 grid gap-2">
            <input
              type="number"
              className="border rounded px-2 py-1 text-sm"
              placeholder="Quantity"
              value={newBatch.quantity}
              onChange={(e) => setNewBatch({ ...newBatch, quantity: e.target.value })}
            />
            <input
              type="number"
              className="border rounded px-2 py-1 text-sm"
              placeholder="Price per unit (₹)"
              value={newBatch.price}
              onChange={(e) => setNewBatch({ ...newBatch, price: e.target.value })}
            />
            <input
              type="date"
              className="border rounded px-2 py-1 text-sm"
              value={newBatch.purchaseDate}
              onChange={(e) => setNewBatch({ ...newBatch, purchaseDate: e.target.value })}
            />
            <input
              type="date"
              className="border rounded px-2 py-1 text-sm"
              placeholder="Expiry (optional)"
              value={newBatch.expiry}
              onChange={(e) => setNewBatch({ ...newBatch, expiry: e.target.value })}
            />
            <button
              onClick={handleAddBatch}
              className="bg-green-600 text-white py-1 rounded text-sm"
            >
              Add Batch
            </button>
          </div>
        </div>

        <div className="mb-4">
          <h2 className="text-sm font-medium mb-1">Existing Batches</h2>
          {selectedItem.expiryBatches.map((b, idx) => (
            <div key={b.id} className="text-xs flex justify-between mb-1 border-b pb-1">
              <span>
                {b.purchaseDate} → Qty {b.quantity} @ ₹{b.price}
                {b.expiry && ` | Exp: ${b.expiry}`}
              </span>
              <button className="text-red-500" onClick={() => handleDeleteBatch(idx)}>
                Delete
              </button>
            </div>
          ))}
        </div>

        <div className="flex gap-2">
          <button className="flex-1 bg-green-600 text-white py-2 rounded" onClick={handleSaveEdit}>
            Save
          </button>
          <button
            className="flex-1 bg-gray-300 py-2 rounded"
            onClick={() => {
              setIsEditing(false);
              setSelectedItem(null);
            }}
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  if (isAdding) {
    return (
      <div className="min-h-screen bg-white text-gray-900 p-4 font-sans">
        <h1 className="text-lg font-bold mb-3">Add Inventory Item</h1>
        <div className="grid gap-3">
          <input
            className="border rounded px-3 py-2"
            placeholder="Item Name"
            value={newItem.name}
            onChange={(e) => setNewItem({ ...newItem, name: e.target.value })}
          />
          <input
            type="number"
            className="border rounded px-3 py-2"
            placeholder="Quantity"
            value={newItem.quantity}
            onChange={(e) => setNewItem({ ...newItem, quantity: e.target.value })}
          />
          <input
            type="number"
            className="border rounded px-3 py-2"
            placeholder="Price per unit (₹)"
            value={newItem.price}
            onChange={(e) => setNewItem({ ...newItem, price: e.target.value })}
          />
          <input
            type="date"
            className="border rounded px-3 py-2"
            value={newItem.purchaseDate}
            onChange={(e) => setNewItem({ ...newItem, purchaseDate: e.target.value })}
          />
          <input
            type="date"
            className="border rounded px-3 py-2"
            placeholder="Expiry (optional)"
            value={newItem.expiry}
            onChange={(e) => setNewItem({ ...newItem, expiry: e.target.value })}
          />
        </div>
        <div className="flex gap-2 mt-4">
          <button
            className="w-full bg-green-600 text-white py-2 rounded-lg text-sm"
            onClick={handleAddItem}
          >
            Add Item
          </button>
          <button
            className="w-full bg-gray-300 text-black py-2 rounded-lg text-sm"
            onClick={() => setIsAdding(false)}
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white text-gray-900 p-4 font-sans">
      <h1 className="text-lg font-bold mb-2">Inventory</h1>
      <p className="text-sm text-gray-600 mb-4">Items currently in stock</p>

      <div className="space-y-3">
        {inventory.map((item) => (
          <div
            key={item.id}
            className="flex items-center justify-between bg-green-50 rounded-xl p-4"
          >
            <div>
              <div className="text-base font-semibold text-green-800">{item.name}</div>
              <div className="text-sm text-gray-600">
                {item.quantity} in stock
                {item.expiryBatches.length > 0 && (
                  <div className="text-xs text-red-500 mt-1">
                    {item.expiryBatches.map((batch, i) => (
                      <div key={i}>
                        {batch.purchaseDate} → {batch.quantity} pcs @ ₹{batch.price}
                        {batch.expiry && ` | Exp: ${batch.expiry}`}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <button
              className="text-sm text-green-700 underline"
              onClick={() => openEdit(item)}
            >
              Edit
            </button>
          </div>
        ))}
      </div>

      <button
        className="fixed bottom-24 right-6 z-20 bg-green-500 text-white w-12 h-12 sm:w-14 sm:h-14 rounded-full shadow-lg text-2xl sm:text-3xl flex items-center justify-center leading-none"
        onClick={() => setIsAdding(true)}
      >
        <span className="pointer-events-none translate-y-[1px]">+</span>
      </button>
    </div>
  );
}
